import React, { useMemo } from "react";

// ─── RADIAL CLOCK ────────────────────────────────────────────────
// Props:
//   elapsed     : number  — seconds elapsed since order was placed
//   maxMinutes  : number  — minutes at which clock = "full" (default 20)

const RadialClock = ({ elapsed = 0, maxMinutes = 20 }) => {
  const { sweepOffset, hourEnd, minEnd, color, trackColor } = useMemo(() => {
    const totalSecs  = maxMinutes * 60;
    const pct        = Math.min(elapsed / totalSecs, 1);

    // urgency color
    const color =
      pct > 0.7 ? "#e24b4a" :
      pct > 0.4 ? "#ba7517" :
                  "#3b6d11";

    const trackColor =
      pct > 0.7 ? "#fcebeb" :
      pct > 0.4 ? "#faeeda" :
                  "#eaf3de";

    // SVG circle: r=13, circumference = 2π×13 ≈ 81.68
    const circumference = 2 * Math.PI * 13;
    const sweepOffset   = circumference * (1 - pct);

    // clock hands — map elapsed seconds onto a 12-hr clock face
    const elapsedMins = elapsed / 60;
    const minDeg      = (elapsedMins % 60) * 6;           // 360° / 60min
    const hourDeg     = ((elapsedMins % 720) / 720) * 360; // 360° / 12hr

    const toXY = (deg, len) => {
      const rad = ((deg - 90) * Math.PI) / 180;
      return {
        x: 16 + Math.cos(rad) * len,
        y: 16 + Math.sin(rad) * len,
      };
    };

    const minEnd  = toXY(minDeg, 7.5);
    const hourEnd = toXY(hourDeg, 5);

    return { sweepOffset, hourEnd, minEnd, color, trackColor, circumference };
  }, [elapsed, maxMinutes]);

  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      style={{ flexShrink: 0 }}
    >
      {/* track ring */}
      <circle
        cx="16" cy="16" r="13"
        fill="none"
        stroke={trackColor}
        strokeWidth="3"
      />
      {/* sweep arc */}
      <circle
        cx="16" cy="16" r="13"
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeDasharray={`${2 * Math.PI * 13}`}
        strokeDashoffset={sweepOffset}
        strokeLinecap="round"
        transform="rotate(-90 16 16)"
        style={{ transition: "stroke-dashoffset 1s linear, stroke 0.4s ease" }}
      />
      {/* hour hand */}
      <line
        x1="16" y1="16"
        x2={hourEnd.x} y2={hourEnd.y}
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        style={{ transition: "x2 1s linear, y2 1s linear, stroke 0.4s ease" }}
      />
      {/* minute hand */}
      <line
        x1="16" y1="16"
        x2={minEnd.x} y2={minEnd.y}
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        style={{ transition: "x2 1s linear, y2 1s linear, stroke 0.4s ease" }}
      />
      {/* center dot */}
      <circle cx="16" cy="16" r="1.5" fill={color} />
    </svg>
  );
};

export default RadialClock;